import { formatTimeInJP } from '../../../../helpers'
import { Prescription } from '../../../../types/models/Presc'
import { PrescImage } from '../../../../types/models/PrescImage'
import {
  RemoteAction,
  RemoteInputOverview,
  RemoteRequest,
  RemoteRequestAsRemote,
  RemoteState,
} from './types'

/** 札の語(画面に出す唯一の場所)。「ステータス」「リクエスト」は出さない(06-language-and-labels.md) */
export const STATE_LABELS: Record<RemoteState, string> = {
  requested: '依頼中',
  accepted: '受領',
  finished: '入力済',
  cancelled: '取り消し',
  declined: 'お断り',
}

/** 操作の語。「完了」は送信と別の一歩(入力はレセコン側の確定まで) */
export const ACTION_LABELS: Record<RemoteAction, string> = {
  request: '遠隔へ',
  cancel: '取り消し',
  accept: '受領',
  decline: '断る',
  finish: '完了',
}

/** 一言を書く窓を挟む操作。送信の主動線と 受領・取り消し には窓を挟まない */
export const DIALOG_ACTIONS: Record<
  Extract<RemoteAction, 'request' | 'decline' | 'finish'>,
  { title: string; placeholder: string; confirmLabel: string }
> = {
  request: {
    title: '遠隔へ',
    placeholder: '一言(任意)',
    confirmLabel: '依頼する',
  },
  decline: { title: '断る', placeholder: '一言(任意)', confirmLabel: '断る' },
  finish: {
    title: '完了',
    placeholder: '申し送り(任意)',
    confirmLabel: '完了',
  },
}

export const isDialogAction = (
  action: RemoteAction
): action is keyof typeof DIALOG_ACTIONS => action in DIALOG_ACTIONS

/** 頼んでいる間(遠隔の店が手を入れうる間)。頼む店の送信ボタンはこの間だけ隠す */
export const isInProgress = (request: RemoteRequest) =>
  request.state === 'requested' || request.state === 'accepted'

/**
 * 操作が通ったときの toast。送信の有無は添えない — 頼む店に戻った紙が未送信なら、「遠隔」行が
 * 「レセコンに未送信」を出し続ける(同じ事実を toast と行の 2 つの書き方で出さない)。遠隔の店の断るは、
 * 送るのが頼む店の番なので言わない。
 */
export const doneMessage = (action: RemoteAction): string => {
  switch (action) {
    case 'request':
      return '遠隔へ頼みました。'
    case 'cancel':
      return '取り消しました。'
    case 'accept':
      return '受領しました。'
    case 'decline':
      return 'お断りしました。'
    case 'finish':
      return '完了しました。'
  }
}

/** 通らなかったときの一言(行の中に出す。toast にしない) */
export const failedMessage = (action: RemoteAction): string => {
  switch (action) {
    case 'request':
      return '頼めませんでした'
    case 'cancel':
      return '取り消せませんでした'
    case 'accept':
      return '受領できませんでした'
    case 'decline':
      return '断れませんでした'
    case 'finish':
      return '完了できませんでした'
  }
}

/** 札が決まった時刻 */
const stateAt = (request: RemoteRequest): string => {
  switch (request.state) {
    case 'finished':
      return request.finished_at ?? request.requested_at
    case 'cancelled':
      return request.cancelled_at ?? request.requested_at
    case 'declined':
      return request.declined_at ?? request.requested_at
    case 'accepted':
      return request.accepted_at ?? request.requested_at
    case 'requested':
      return request.requested_at
  }
}

/**
 * 人の言葉の段に出すのは**相手の店の言葉だけ**(自分が書いた一言を自分に見せ返さない。誰の言葉かを
 * 読み分けさせない)。遠隔の店 = 頼む店の一言。頼む店 = 遠隔の店の申し送り(入力済)・断りの一言(お断り)
 */
const otherStoreNote = (
  request: RemoteRequest,
  side: 'remote' | 'requester'
): string => {
  if (side === 'remote') return request.request_note
  switch (request.state) {
    case 'finished':
      return request.handover_note
    case 'declined':
      return request.decline_note
    default:
      return ''
  }
}

/**
 * 「遠隔」行の中の文。1 段 = 主語 1 つで返す(design-book 04-apps/mamoru.md「遠隔入力の『遠隔』行」)。
 * 画面は上から `head`(依頼)・`note`(人の言葉)・`sent`(紙とレセコン)の段に置き、空の段は出さない。
 *
 * - `head`: 札と時刻(「依頼中 08:18」)。遠隔の店には誰からの依頼かを頭に付ける(「東口店から依頼 08:18」
 *   「東口店から依頼 · 入力済 08:40」)。時刻は一覧と同じ HH:MM だけ(経過は出さない)
 * - `note`: 相手の店の言葉(「」付き)。遠隔の店には頼む店の一言、頼む店には申し送り・断りの一言
 * - `sent`: 読む人が送信を押す番のときだけ出す。遠隔の店 = 依頼中・受領の間に、依頼の後で送っていたら
 *   「レセコンに送信済 HH:MM」(依頼より前の自動送信は数えない。完了の前に直した内容を送ったかの確かめ)。頼む店 = 戻った紙
 *   (取り消し・お断り)が未送信なら「レセコンに未送信」(送信ボタンを押す番)
 */
export type RequestText = { head: string[]; note: string; sent: string }

export const describeRequest = (
  paper:
    | { side: 'remote'; request: RemoteRequestAsRemote }
    | { side: 'requester'; request: RemoteRequest }
): RequestText => {
  const { request } = paper
  const state = `${STATE_LABELS[request.state]} ${formatTimeInJP(
    stateAt(request)
  )}`
  const note = otherStoreNote(request, paper.side)
  if (paper.side === 'remote') {
    const from = `${paper.request.from_label}から依頼`
    const sentAfterRequest =
      isInProgress(request) &&
      request.sent_at !== null &&
      new Date(request.sent_at) >= new Date(request.requested_at)
    return {
      head:
        request.state === 'requested'
          ? [`${from} ${formatTimeInJP(request.requested_at)}`]
          : [from, state],
      note: note ? `「${note}」` : '',
      sent:
        sentAfterRequest && request.sent_at
          ? `レセコンに送信済 ${formatTimeInJP(request.sent_at)}`
          : '',
    }
  }
  const isReturned =
    request.state === 'cancelled' || request.state === 'declined'
  return {
    head: [state],
    note: note ? `「${note}」` : '',
    sent: isReturned && !request.sent_at ? 'レセコンに未送信' : '',
  }
}

/**
 * 紙 1 枚から見た遠隔入力。出し分けは `as_remote` / `as_requester` のどちらに紙があるかだけ。
 * - remote: 頼まれた紙(遠隔の店の画面)
 * - requester: 依頼の行がある自店の紙(依頼先が消えた後も。戻った紙は店が送信ボタンで送る)、
 *   または依頼先がある店の自店の紙(依頼の行はまだ無い)
 * - null: それ以外。「遠隔」行は出さない
 */
export type PaperRemoteInput =
  | { side: 'remote'; request: RemoteRequestAsRemote }
  | { side: 'requester'; request: RemoteRequest | null }
  | { side: null; request: null }

export type RemoteInputIndex = {
  overview: RemoteInputOverview | undefined
  requesterByEvent: Map<string, RemoteRequest>
  remoteByEvent: Map<string, RemoteRequestAsRemote>
  /** 頼まれた紙(自店の一覧に混ぜる) */
  remoteRows: Prescription[]
  /** 遠隔の店の「新着」= まだ受領していない依頼の数 */
  newCount: number
}

export const indexOverview = (
  overview: RemoteInputOverview | undefined
): RemoteInputIndex => ({
  overview,
  requesterByEvent: new Map(
    (overview?.as_requester ?? []).map((r) => [r.event_id, r])
  ),
  remoteByEvent: new Map(
    (overview?.as_remote ?? []).map((r) => [r.event_id, r])
  ),
  remoteRows: (overview?.as_remote ?? []).map((r) => r.prescription),
  newCount: (overview?.as_remote ?? []).filter((r) => r.state === 'requested')
    .length,
})

export const lookupPaper = (
  index: RemoteInputIndex,
  eventId: string
): PaperRemoteInput => {
  const remote = index.remoteByEvent.get(eventId)
  if (remote) return { side: 'remote', request: remote }
  const own = index.requesterByEvent.get(eventId)
  if (own) return { side: 'requester', request: own }
  if (index.overview?.route) return { side: 'requester', request: null }
  return { side: null, request: null }
}

/**
 * 「レセコンに送信」を出すか。
 *
 * - 頼まれた紙: 常に出す(店の設定 `show_save_csv_button` を見ない。back の門も依頼の行がある紙を通す)
 * - 依頼の行がある自店の紙: 依頼中・受領の間だけ隠し、それ以外は設定を問わず出す。
 *   戻った紙は頼む店が送信ボタンで送るので、自動送信だけの店でも出す
 * - それ以外: 今までどおり店の設定
 */
export const showsSendButton = (
  paper: PaperRemoteInput,
  storeHasButton: boolean
): boolean => {
  if (paper.side === 'remote') return true
  if (paper.request) return !isInProgress(paper.request)
  return storeHasButton
}

/**
 * 自店の一覧が、遠隔の店が直す前の版を持っている紙があるか。
 *
 * 自店の一覧(2 秒)は新しい紙だけを差分で取るので、遠隔の店が作った新しい版は届かない。
 * 古い版のまま送ると直す前の JAHIS が出て、直すと古い版から新しい版ができて遠隔の店の修正が消える。
 */
export const hasStaleOwnPaper = (
  overview: RemoteInputOverview | undefined,
  own: Prescription[] | undefined
): boolean => {
  if (!overview || !own) return false
  const ownVersion = new Map(
    own.map((p) => [p.ocr_request_event_id, p.edit_version])
  )
  return overview.as_requester.some((r) => {
    const mine = ownVersion.get(r.event_id)
    return (
      mine !== undefined &&
      r.latest_edit_version != null &&
      mine < r.latest_edit_version
    )
  })
}

/** 前の画像の URL を使い続けるのは、署名の期限までこれ以上残っている間だけ */
const KEEP_IMAGE_URL_MIN_MS = 10 * 60 * 1000

/** CloudFront の署名付き URL の期限(`Expires` = epoch 秒)。署名の無い URL(手元の MinIO)は無期限 */
const urlExpiresAt = (url: string): number => {
  try {
    const expires = new URL(url).searchParams.get('Expires')
    return expires ? Number(expires) * 1000 : Infinity
  } catch {
    return Infinity
  }
}

const sameImages = (a: PrescImage[], b: PrescImage[]): boolean =>
  a.length === b.length && a.every((image, i) => image.id === b[i].id)

/**
 * 頼まれた紙の画像は、id が同じで署名の期限が十分残っていれば前の取得の配列をそのまま使う。
 *
 * 画像の URL は取得のたびに署名し直されて文字列が変わる(ImagePane.tsx)。5 秒の口でそのまま
 * 差し替えると、遠隔の店が開いている紙の `<img src>` が 5 秒ごとに変わって画像を読み込み直す。
 * 画像は版をまたいで変わらない(id も同じ)。期限が近づいたら新しい URL に替える(期限は 60 分)。
 */
export const keepRemoteImages = (
  prev: RemoteInputOverview | undefined,
  next: RemoteInputOverview,
  now: number
): RemoteInputOverview => {
  if (!prev) return next
  const prevImages = new Map(
    prev.as_remote.map((r) => [r.event_id, r.prescription.presc_images])
  )
  return {
    ...next,
    as_remote: next.as_remote.map((r) => {
      const kept = prevImages.get(r.event_id)
      const keeps =
        kept &&
        sameImages(kept, r.prescription.presc_images) &&
        kept.every(
          (image) => urlExpiresAt(image.image_url) - now > KEEP_IMAGE_URL_MIN_MS
        )
      return keeps
        ? { ...r, prescription: { ...r.prescription, presc_images: kept } }
        : r
    }),
  }
}

/** 一覧の行の右に出す 12px の語。遠隔の店には頼む店の呼び名、頼む店には札 */
export const rowBadge = (paper: PaperRemoteInput): string | undefined => {
  if (paper.side === 'remote') return paper.request.from_label
  // 取り消しは自店がした操作で、紙は普段に戻っている。一覧に札を残さない
  if (paper.request && paper.request.state !== 'cancelled')
    return STATE_LABELS[paper.request.state]
  return undefined
}

/** 入力済は行ごと沈める(輪郭のドット + fg.muted)。消さない */
export const isSunkRow = (paper: PaperRemoteInput): boolean =>
  paper.request?.state === 'finished'
